import { cn } from '@/lib/utils';

/** Button-style on/off toggle (no shadcn switch primitive in this app). */
export const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cn(
      'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
      checked ? 'bg-teal-600' : 'bg-gray-200',
    )}
  >
    <span
      className={cn(
        'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
        checked ? 'translate-x-5' : 'translate-x-0.5',
      )}
    />
  </button>
);

/** A labelled row: description on the left, control on the right. */
export const FieldRow: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="flex items-center justify-between gap-4 py-3">
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

/** Save/reset row pinned under an editable section. */
export const SaveRow: React.FC<{
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onReset: () => void;
  error?: string | null;
}> = ({ dirty, saving, onSave, onReset, error }) => (
  <div className="mt-4 flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
    {error && <span className="mr-auto text-xs text-red-600">{error}</span>}
    <button
      type="button"
      onClick={onReset}
      disabled={!dirty || saving}
      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40"
    >
      Reset
    </button>
    <button
      type="button"
      onClick={onSave}
      disabled={!dirty || saving}
      className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:opacity-40"
    >
      {saving ? 'Saving…' : 'Save changes'}
    </button>
  </div>
);
